// Trading Psychology Module — 3 long lessons in English.
// Each lesson has structured `contenido` (when there are sub-items) or
// plain markdown `contenido_texto` for the body. The page detail supports
// both formats.

export const LECCIONES_PSICOLOGIA = [
  {
    id: "los-7-errores",
    numero: 1,
    titulo: "The 7 Psychological Mistakes That Destroy Accounts",
    duracion: "12 min",
    descripcion: "95% of traders lose money. It's not due to lack of technical knowledge — it's due to lack of mental discipline. These are the 7 most common psychological mistakes and how to avoid them.",
    contenido: [
      { tipo: "error", numero: 1, nombre: "FOMO (Fear Of Missing Out)",
        descripcion: "Entering a trade because the price has already risen a lot and you're afraid of missing the move. Result: you buy at highs and you're the last person to enter before the price drops.",
        sintomas: ["You enter without analyzing", "The price already rose >5%", "You don't have a defined SL plan", "You feel urgency"],
        solucion: "Accept that there will always be another opportunity. If you didn't enter at the start of the move, the trade IS NO LONGER YOURS. Wait for the next setup.",
        ejercicio: "The next time you feel FOMO, write in your journal: «The price already rose X%. If I enter now, my R:R is ___». If the R:R is less than 1:2, DON'T enter." },
      { tipo: "error", numero: 2, nombre: "Revenge Trading",
        descripcion: "After a loss, you immediately open another trade to recover the lost money. You're trading from emotion, not from strategy. Result: the second loss is usually bigger than the first.",
        sintomas: ["You just lost a trade", "You enter without waiting for setup", "You increase position size", "You feel anger or frustration"],
        solucion: "Unbreakable rule: after 2 consecutive losses, STOP trading that day. The market will be there tomorrow.",
        ejercicio: "Implement the 2-stop rule: if you hit 2 stop losses in one day, close the platform. No exceptions." },
      { tipo: "error", numero: 3, nombre: "Moving the Stop Loss",
        descripcion: "The price approaches the stop loss and instead of letting it trigger, you move it lower «hoping it will recover». Result: a 1% loss turns into a 5-10% loss.",
        sintomas: ["The price approaches the SL", "You think «just a little more»", "You've moved the SL more than once", "The trade lasts much longer than planned"],
        solucion: "The stop loss is SACRED. If you placed it in the right spot when analyzing, don't move it. If you move it, all your risk management becomes useless.",
        ejercicio: "Put a note on your monitor: «SL = PLAN. PLAN = SL. DO NOT MOVE»." },
      { tipo: "error", numero: 4, nombre: "Overtrading",
        descripcion: "Trading excessively, constantly looking for trades. Boredom or euphoria lead to entering low-quality setups. More trades = more commissions = more risk exposure = more mental exhaustion.",
        sintomas: ["You have more than 5 trades in one day", "You actively search for trades", "You change timeframes looking for setups", "You can't be without an open position"],
        solucion: "Quality over quantity. Define your daily maximum number of trades (recommended: 2-3). If you've reached it, stop. The best traders make few trades but very selective ones.",
        ejercicio: "Keep a record of all your trades. At the end of the month, compare win rate of first trades of the day vs the last ones. You'll see that the last ones are worse." },
      { tipo: "error", numero: 5, nombre: "Exiting Before Take Profit",
        descripcion: "The trade is going well, you're up +3% and you decide to close before reaching TP because «you already made enough» or fear it will reverse. Result: your real R:R is 1:0.8 instead of 1:3.",
        sintomas: ["You close winning trades early", "You're afraid the profit will disappear", "Your average profit is less than your average risk"],
        solucion: "Trust your analysis. If you placed the TP at a valid technical level, let it run. You can use partials (close 50% at the first target) to reduce anxiety.",
        ejercicio: "In your journal, note the price at which you closed and the price it actually reached. Quantify how much you «left on the table» by not respecting the TP." },
      { tipo: "error", numero: 6, nombre: "Overconfidence After Winning Streaks",
        descripcion: "After 5-6 winning trades in a row you increase position size or lower your analysis standards because «you're on a roll». Pride comes before the fall. Always.",
        sintomas: ["You have several winning trades", "You increase size without justification", "You analyze each trade less", "You feel like «you can't lose»"],
        solucion: "The market doesn't know you're on a winning streak. Each trade is independent. Always maintain the same % risk.",
        ejercicio: "Define in advance when you'll increase position size: only when capital has grown by 20%. Not before." },
      { tipo: "error", numero: 7, nombre: "Not Following the Trading Plan",
        descripcion: "You have a plan but when it's time to trade you ignore it because «this time is different». No plan = no discipline = no consistent results. The plan is not a suggestion, it's the law.",
        sintomas: ["You change strategy in the moment", "You trade on intuition", "Your results are inconsistent", "You don't have written rules"],
        solucion: "Write your trading rules on paper and put them in front of your monitor. Before each trade, verify it meets ALL conditions. If it doesn't meet them, there's no trade.",
        ejercicio: "Write your trading plan right now: entry conditions, SL, TP, maximum trades per day, maximum capital to risk. One page, simple rules, no exceptions." },
    ],
  },
  {
    id: "reglas-trading-personal",
    numero: 2,
    titulo: "How to Create Your Personal Trading Rulebook",
    duracion: "8 min",
    descripcion: "A trader without rules is a gambler. The difference between a professional trader and a loser is not technical knowledge — it's the discipline to follow a set of rules under any market condition.",
    contenido_texto: `A trader without rules is a gambler. The difference between a professional trader and a loser is not technical knowledge — it's the discipline to follow a set of rules under any market condition.

YOUR RULES MUST INCLUDE:

1. ENTRY CRITERIA
   «I only enter when: [list your specific conditions]».
   Example: «I only enter in uptrend (4H), on pullback to OB, with CHoCH confirmation on 15m, with minimum R:R 1:2».

2. RISK MANAGEMENT
   • My maximum risk per trade is X%.
   • My maximum daily loss is X%.
   • If I lose X% in the week, I don't trade more that week.

3. MARKET CONDITIONS
   • I don't trade 30 minutes before important news.
   • I don't trade on Mondays before 9 AM.
   • I only trade during London or New York session.

4. EMOTIONAL MANAGEMENT
   • If I hit 2 stops in a row, I stop that day.
   • I review the plan before each trade.
   • I don't trade if I'm tired, angry or stressed.

5. REVIEW AND IMPROVEMENT
   • Every Sunday I review all trades from the week.
   • Every month I calculate my statistics: win rate, avg R:R, drawdown.

Print your plan. Stick it where you'll see it every day. Read it before each session. If you follow it for 90 days, you'll become a different trader.`,
  },
  {
    id: "diario-emocional",
    numero: 3,
    titulo: "The Emotional Trading Journal",
    duracion: "6 min",
    descripcion: "The best traders in the world keep a journal. They don't just record the numbers (entry, exit, P&L) but also their mental state.",
    contenido_texto: `The best traders in the world keep a journal. They don't just record the numbers (entry, exit, P&L) but also their mental state.

JOURNAL ENTRY TEMPLATE:

DATE: _______________
EMOTIONAL STATE BEFORE TRADING (1-10): ___
Notes: _______________

TRADE
• Pair: ___ — Direction: LONG / SHORT
• Analysis timeframe: ___
• Entry reason: _______________
• Setup identified: _______________
• Entry: ___  ·  SL: ___  ·  TP: ___
• Calculated R:R: ___

TRADE MANAGEMENT
• Did you follow the plan? YES / NO
• If not: Why? _______________
• Emotions during the trade: _______________

RESULT
• Exit price: ___  ·  P&L: ___
• Did you respect the SL? YES / NO
• Did you respect the TP? YES / NO

TRADE LESSON
• What did you do well? _______________
• What would you improve? _______________

Repeat it for every trade. After 50 trades, the patterns that destroy your account will become obvious. And then you can fix them.`,
  },
];

export const findLeccion = (id) =>
  LECCIONES_PSICOLOGIA.find((l) => l.id === id);